import { makeStyles, mergeClasses } from '@fluentui/react-components';
import type { ReactNode } from 'react';

export type DonutSegment = {
    fraction: number;
    color: string;
};

export type DonutChartProps = {
    size: number;
    strokeWidth: number;
    segments: DonutSegment[];
    trackColor: string;
    children?: ReactNode;
    className?: string;
};

export const DonutChart: React.FC<DonutChartProps> = ({ size, strokeWidth, segments, trackColor, children, className }) => {
    const classes = useStyles();
    const radius = (size - strokeWidth) / 2;
    const center = size / 2;
    const circumference = 2 * Math.PI * radius;

    let cumulative = 0;

    return (
        <div className={mergeClasses(classes.wrapper, className)} style={{ width: size, height: size }}>
            <svg className={classes.svg} width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
                <circle cx={center} cy={center} r={radius} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
                {segments.map((seg, i) => {
                    if (seg.fraction <= 0) {
                        return null;
                    }
                    const dash = seg.fraction * circumference;
                    const offset = -cumulative * circumference;
                    cumulative += seg.fraction;
                    return (
                        <circle
                            key={i}
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="none"
                            stroke={seg.color}
                            strokeWidth={strokeWidth}
                            strokeLinecap="butt"
                            strokeDasharray={`${dash} ${circumference}`}
                            strokeDashoffset={offset}
                            transform={`rotate(-90 ${center} ${center})`}
                        />
                    );
                })}
            </svg>
            {children && <div className={classes.center}>{children}</div>}
        </div>
    );
};

const useStyles = makeStyles({
    wrapper: {
        position: 'relative',
        flexShrink: 0,
    },
    svg: {
        display: 'block',
    },
    center: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
    },
});
